/**
 * PostHog Analytics Helpers
 * Utility functions for tracking events with attribution data
 */
import { buildEventAttributionProperties, getAttributionData } from "./utm-tracker";

/**
 * Track an event with automatic attribution data
 */
export function trackEventWithAttribution(
  posthog: any,
  eventName: string,
  properties?: Record<string, any>,
  captureOptions?: { transport?: "XHR" | "fetch" | "sendBeacon" }
) {
  if (!posthog) return;

  const attribution = getAttributionData();

  const eventProperties = {
    ...properties,
    ...buildEventAttributionProperties(attribution),
  };

  if (captureOptions) {
    posthog.capture(eventName, eventProperties, captureOptions);
    return;
  }

  posthog.capture(eventName, eventProperties);
}

/**
 * Track upgrade CTA interaction with richer context.
 */
export function trackUpgradeIntent(posthog: any, source: string, properties?: Record<string, any>) {
  trackEventWithAttribution(posthog, "upgrade_cta_clicked", {
    source,
    ...properties,
  });
}

/**
 * Track checkout started event
 */
export function trackCheckoutStarted(posthog: any, priceId: string, amount?: number) {
  trackEventWithAttribution(posthog, "checkout_started", {
    price_id: priceId,
    amount,
  });
}

/**
 * Track checkout completed event
 */
export function trackCheckoutCompleted(posthog: any, sessionId: string, amount?: number) {
  trackEventWithAttribution(posthog, "checkout_completed", {
    session_id: sessionId,
    amount: amount,
  });
}

/**
 * Track subscription activation post-purchase (client confirmation or webhook).
 */
export function trackSubscriptionActivated(
  posthog: any,
  planId: string,
  amount?: number,
  cadence?: "monthly" | "yearly" | "lifetime",
  properties?: Record<string, any>
) {
  trackEventWithAttribution(posthog, "subscription_activated", {
    plan_id: planId,
    amount,
    billing_cadence: cadence,
    ...properties,
  });
}

/**
 * Track feature usage with attribution
 */
export function trackFeatureUsed(
  posthog: any,
  featureName: string,
  metadata?: Record<string, any>
) {
  trackEventWithAttribution(posthog, "feature_used", {
    feature: featureName,
    ...metadata,
  });
}

/**
 * Track AI interaction events
 */
export function trackAIInteraction(
  posthog: any,
  interactionType: "voice" | "text" | "chat",
  duration?: number
) {
  trackEventWithAttribution(posthog, "ai_interaction", {
    interaction_type: interactionType,
    duration: duration,
  });
}

/**
 * Track user engagement milestones
 */
export function trackMilestone(posthog: any, milestoneName: string, value?: number) {
  trackEventWithAttribution(posthog, "milestone_reached", {
    milestone: milestoneName,
    value: value,
  });
}

/**
 * Server-side event tracking helper for API routes
 */
export function createServerEventProperties(attribution?: Record<string, any>) {
  if (!attribution) return {};

  return {
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    utm_term: attribution.utm_term,
    utm_content: attribution.utm_content,
    initial_referrer: attribution.initial_referrer,
    initial_landing_page: attribution.initial_landing_page,
  };
}
